import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Typography from "@mui/material/Typography";
import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import type { NavArea } from "./navAreas";
import { pathMatches } from "./navItems";
import { NAV_ICON_SIZE, sectionNavListSx } from "./styles";

export const SECTION_COLUMN_WIDTH = 216;

/**
 * The pages of the sidebar area the user is in (Platform, Organization, ...),
 * in a column between the sidebar and the page, so a sibling is one click away.
 */
export default function SectionColumn({ area }: { area: NavArea }) {
  const { pathname } = useLocation();
  // The longest matching link, so /platform does not also light up under /platform/x.
  const current = area.sections
    .flatMap((s) => s.items)
    .filter((item) => pathMatches(pathname, item.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return (
    <Box
      component="nav"
      aria-label={`${area.label} pages`}
      sx={{
        width: SECTION_COLUMN_WIDTH,
        flexShrink: 0,
        position: "sticky",
        top: 0,
        height: "100vh",
        overflowY: "auto",
        borderRight: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: 52, px: 2.25 }}>
        <Box sx={{ display: "flex", color: "text.secondary", "& svg": { fontSize: NAV_ICON_SIZE } }}>{area.icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {area.label}
        </Typography>
      </Box>
      <List disablePadding sx={{ ...sectionNavListSx, pt: 0 }}>
        {area.sections.map((section, i) => (
          <Fragment key={section.heading ?? i}>
            {/* "Platform" under a "Platform" title says nothing. */}
            {section.heading && section.heading !== area.label && (
              <ListSubheader disableSticky>{section.heading}</ListSubheader>
            )}
            {section.items.map((item) => (
              <ListItemButton key={item.path} component={Link} to={item.path} selected={item === current}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </Fragment>
        ))}
      </List>
    </Box>
  );
}
